/**
 * useConflictCheck
 *
 * Drives FLOW-573's contradiction detector for one capture surface: run a
 * check against the draft or saved decision actually being submitted, hold
 * the resulting warnings, and record how a human disposes of each one.
 *
 * Detection enriches; it never gates (see plan Global Constraints). Both an
 * `unavailable` detection status and a thrown request are surfaced as the
 * same `"unavailable"` state so a caller can treat "could not determine" as
 * "proceed" without special-casing errors.
 *
 * Applying the human's dispositions lives here too (`applyOutcomes`) rather
 * than in each capture surface: both run the same edges-then-resolve sequence,
 * and the two hand-written copies had each drifted in a different way.
 *
 * `check` is deduplicated against the last-checked subject: every real check
 * persists warning rows server-side, and re-checking an unchanged subject
 * would inflate the `shown` denominator the acceptance rate is measured
 * against (Global Constraints).
 */

import { Accessor, createSignal } from "solid-js";
import type {
  ConflictCheckRequest,
  ConflictResolution,
  DecisionConflictWarning,
  DecisionEdgeRelationship,
  DecisionId,
  ProjectId,
} from "../api/types";
import {
  checkDecisionConflicts,
  resolveConflictWarning,
} from "../api/decisionConflicts";
import { createDecisionEdge } from "../api/decisions";
import { ApiError } from "../api/client";
import { describeAiError, surfaceQuotaExhausted } from "../utils/aiError";

export type ConflictCheckState =
  | "idle"
  | "checking"
  | "clear"
  | "warnings"
  | "unavailable";

export interface UseConflictCheckResult {
  state: Accessor<ConflictCheckState>;
  warnings: Accessor<DecisionConflictWarning[]>;
  error: Accessor<string | null>;
  /** Runs a check unless this exact subject was already checked. Resolves to the new state. */
  check: (subject: ConflictCheckRequest) => Promise<ConflictCheckState>;
  /** Records one disposition. A 409 counts as already-resolved, not an error. */
  resolve: (
    warningId: string,
    outcome: ConflictResolution,
    note?: string,
  ) => Promise<void>;
  /**
   * Applies every chosen disposition for the current warnings, once (D2):
   * the edges an acceptance implies (D3) for all of them, then the resolves
   * (D4 — the consequence lands before it is measured).
   *
   * `decisionId` is the decision the acceptances are on behalf of. Pass
   * `null` when no decision was saved — the `accepted_edited` path — and only
   * `accepted_edited` is recorded, since returning to the edit form is the
   * only consequence that actually happened. Anything else stays pending for
   * the re-check D1 guarantees.
   */
  applyOutcomes: (
    decisionId: DecisionId | null,
    choices: Record<string, ConflictResolution | undefined>,
  ) => Promise<void>;
  reset: () => void;
}

function sameSubject(
  a: ConflictCheckRequest,
  b: ConflictCheckRequest,
): boolean {
  return (
    a.decision_id === b.decision_id &&
    a.title === b.title &&
    a.rationale === b.rationale &&
    a.context === b.context &&
    a.category === b.category &&
    a.scope === b.scope
  );
}

/**
 * The edge an accepted warning implies (D3). `accepted_edited` and both
 * `dismissed_*` outcomes deliberately have none.
 */
const EDGE_FOR_OUTCOME: Partial<
  Record<ConflictResolution, DecisionEdgeRelationship>
> = {
  accepted_superseded: "supersedes",
  accepted_linked: "conflicts_with",
};

export function useConflictCheck(
  projectId: Accessor<ProjectId>,
): UseConflictCheckResult {
  const [state, setState] = createSignal<ConflictCheckState>("idle");
  const [warnings, setWarnings] = createSignal<DecisionConflictWarning[]>([]);
  const [error, setError] = createSignal<string | null>(null);
  let checkedSubject: ConflictCheckRequest | null = null;

  const check = async (
    subject: ConflictCheckRequest,
  ): Promise<ConflictCheckState> => {
    if (checkedSubject && sameSubject(checkedSubject, subject)) {
      return state();
    }

    setState("checking");
    setError(null);
    try {
      const response = await checkDecisionConflicts(projectId(), subject);
      checkedSubject = subject;

      if (response.detection_status === "unavailable") {
        setWarnings([]);
        setState("unavailable");
        return "unavailable";
      }

      setWarnings(response.warnings);
      const next = response.warnings.length > 0 ? "warnings" : "clear";
      setState(next);
      return next;
    } catch (err) {
      checkedSubject = null;
      setWarnings([]);
      surfaceQuotaExhausted(err);
      setError(describeAiError(err));
      setState("unavailable");
      return "unavailable";
    }
  };

  const resolve = async (
    warningId: string,
    outcome: ConflictResolution,
    note?: string,
  ): Promise<void> => {
    try {
      const updated = await resolveConflictWarning(projectId(), warningId, {
        outcome,
        note,
      });
      setWarnings((prev) =>
        prev.map((warning) => (warning.id === warningId ? updated : warning)),
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        // Already resolved — by a concurrent session, or by a retry racing
        // itself. The first opinion counted, and the 409 does not say which
        // one that was. So record that the warning is settled without
        // claiming this caller's outcome won it; the authoritative value
        // arrives with the next `listConflictWarnings`.
        const resolvedAt = new Date().toISOString();
        setWarnings((prev) =>
          prev.map((warning) =>
            warning.id === warningId
              ? { ...warning, resolved_at: resolvedAt }
              : warning,
          ),
        );
        return;
      }
      surfaceQuotaExhausted(err);
      setError(describeAiError(err));
      throw err;
    }
  };

  const applyOutcomes = async (
    decisionId: DecisionId | null,
    choices: Record<string, ConflictResolution | undefined>,
  ): Promise<void> => {
    const applicable = warnings().flatMap((warning) => {
      const outcome = choices[warning.id];
      if (!outcome) return [];
      // Nothing was saved, so the only disposition whose consequence landed
      // is `accepted_edited` — returning to the edit form.
      if (decisionId === null && outcome !== "accepted_edited") return [];
      return [{ warning, outcome }];
    });

    if (decisionId !== null) {
      for (const { warning, outcome } of applicable) {
        const relationship = EDGE_FOR_OUTCOME[outcome];
        if (relationship && warning.existing_decision_id) {
          await createDecisionEdge(projectId(), decisionId, {
            target_decision_id: warning.existing_decision_id,
            relationship,
          });
        }
      }
    }

    for (const { warning, outcome } of applicable) {
      await resolve(warning.id, outcome);
    }
  };

  const reset = () => {
    setState("idle");
    setWarnings([]);
    setError(null);
    checkedSubject = null;
  };

  return { state, warnings, error, check, resolve, applyOutcomes, reset };
}
